import { useState } from "react";
import { Moon, Sparkles, CheckCircle2, Clock, AlertTriangle, ChevronDown } from "lucide-react";
import { formatTime, formatDuration, formatPrice, arrivesNextDay, stopsLabel } from "@/components/trips/itinerary/itinerary-utils";
import type { PlanFlight, PlanLegVerdict, PriceHistory } from "@/lib/types";
import AirlineLogo from "./AirlineLogo";
import SourceLed from "./SourceLed";
import FlightDetail from "./FlightDetail";
import PriceHistoryStrip from "./PriceHistoryStrip";

interface FocusFlightCardProps {
  focus: PlanFlight | null;
  verdict: PlanLegVerdict | null;
  /** Detailed focus-flight price history (now/low/high/trend/checks/span). */
  history?: PriceHistory;
  /** Origin airport IATA (from the leg) — for the detail timeline. */
  originCode?: string | null;
  /** Destination airport IATA (from the leg) — for the detail timeline. */
  destinationCode?: string | null;
}

const VERDICT_TONE: Record<PlanLegVerdict["status"], { tone: string; Icon: typeof CheckCircle2 }> = {
  clears: { tone: "text-emerald-700", Icon: CheckCircle2 },
  tight: { tone: "text-amber-700", Icon: Clock },
  too_late: { tone: "text-red-700", Icon: AlertTriangle },
};

export default function FocusFlightCard({
  focus,
  verdict,
  history,
  originCode,
  destinationCode,
}: FocusFlightCardProps) {
  const [detailOpen, setDetailOpen] = useState(false);

  // No flights chosen yet for this leg.
  if (!focus) {
    return (
      <div className="mt-2 flex items-center gap-2 rounded-lg border border-dashed bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
        <Clock className="h-4 w-4 animate-pulse" aria-hidden="true" />
        Searching flights…
      </div>
    );
  }

  const airlineName = focus.airlineName || focus.airline || "Flight";
  const flightLabel = focus.flightNumber
    ? airlineName + " · Flight " + focus.flightNumber
    : airlineName;

  const departT = formatTime(focus.departLocal);
  const arriveT = formatTime(focus.arriveLocal);
  // Arrival lands the next calendar day (e.g. 8:20 PM → 5:43 AM +1).
  const nextDay = arrivesNextDay(focus.departDate, focus.arriveDate);
  const arriveDisplay = arriveT && nextDay ? arriveT + " +1" : arriveT;
  const times =
    departT && arriveDisplay
      ? departT + " – " + arriveDisplay
      : departT || arriveDisplay || null;

  const meta: { text: string; mono: boolean }[] = [];
  if (focus.stops != null) meta.push({ text: stopsLabel(focus.stops), mono: false });
  const dur = formatDuration(focus.durationMinutes);
  if (dur) meta.push({ text: dur, mono: true });

  // Neutral moon indicator: red-eye departure OR overnight (next-day) arrival.
  const showMoon = focus.redEye || focus.overnight;
  const moonLabel = focus.redEye ? "red-eye" : "overnight";
  const moonTitle = focus.redEye ? "Red-eye flight" : "Arrives the next day";

  const v = verdict ? VERDICT_TONE[verdict.status] : null;

  return (
    <div className="mt-2 rounded-lg border bg-card p-4">
      <div className="flex items-start gap-3">
        <AirlineLogo iata={focus.airline} logoUrl={focus.logoUrl} size={40} />

        <div className="min-w-0 flex-1">
          {/* Airline name + flight number + preference pill */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-semibold text-foreground">{flightLabel}</span>
            {focus.matchesPreferences && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--primary))]/[0.12] px-2 py-0.5 text-[11px] font-medium text-[hsl(var(--accent-foreground))]">
                <Sparkles className="h-3 w-3" aria-hidden="true" />
                matches your preferences
              </span>
            )}
          </div>

          {/* Times · stops · duration + red-eye moon */}
          {(times || meta.length > 0) && (
            <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm text-muted-foreground">
              {times && <span className="font-mono tabular-nums">{times}</span>}
              {meta.map((m, i) => (
                <span key={i} className="flex items-center gap-1.5">
                  <span aria-hidden="true">·</span>
                  <span className={"tabular-nums" + (m.mono ? " font-mono" : "")}>{m.text}</span>
                </span>
              ))}
              {showMoon && (
                <span
                  className="ml-0.5 inline-flex items-center gap-1 rounded-full bg-indigo-50 px-1.5 py-0.5 text-[11px] font-medium text-indigo-600"
                  title={moonTitle}
                >
                  <Moon className="h-3 w-3" aria-hidden="true" />
                  {moonLabel}
                </span>
              )}
            </div>
          )}

          {/* Perk line */}
          {focus.perk && (
            <div className="mt-1 text-xs text-muted-foreground">{focus.perk.label}</div>
          )}
        </div>

        {/* Price + source LED + detail expander */}
        <div className="flex shrink-0 flex-col items-end gap-1">
          <div className="flex items-center gap-1.5">
            <span className="font-mono text-lg font-bold tabular-nums text-foreground">
              {formatPrice(focus.price) ?? "—"}
            </span>
            <SourceLed source={focus.source} />
          </div>
          <button
            type="button"
            onClick={() => setDetailOpen((o) => !o)}
            aria-expanded={detailOpen}
            className="inline-flex items-center gap-0.5 rounded text-[11px] font-medium text-[hsl(var(--primary))] transition-colors hover:underline"
          >
            {detailOpen ? "Hide details" : "Flight details"}
            <ChevronDown
              className={"h-3.5 w-3.5 transition-transform " + (detailOpen ? "rotate-180" : "")}
              aria-hidden="true"
            />
          </button>
        </div>
      </div>

      {/* Verdict line */}
      {verdict && v && (
        <div className={"mt-2 flex items-center gap-1.5 text-sm font-medium " + v.tone}>
          <v.Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{verdict.detail}</span>
        </div>
      )}

      {/* Detailed price-history strip — the container's "history graph". */}
      {history && (
        <div className="mt-2">
          <PriceHistoryStrip history={history} />
        </div>
      )}

      {/* Expandable flight detail (Google-Flights pattern, OUR data only) */}
      {detailOpen && (
        <div className="mt-3">
          <FlightDetail
            flight={focus}
            originCode={originCode ?? null}
            destinationCode={destinationCode ?? null}
          />
        </div>
      )}
    </div>
  );
}
